import React, { useState } from 'react';
import { StrategyOption } from '../../types';
import {
    Box,
    Typography,
    Paper,
    Collapse,
    Chip,
    Tooltip,
} from '@mui/material';
import {
    LocalShipping as TruckIcon,
    Bolt as ZapIcon,
    Shield as ShieldIcon,
    Inventory as BoxIcon,
    CheckCircle as CheckCircleIcon,
    Timeline as ActivityIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    AutoAwesome as SparklesIcon,
} from '@mui/icons-material';

interface StrategyCardProps {
    strategy: StrategyOption;
    isSelected: boolean;
    onSelect: (id: string) => void;
}

// Theme configuration per strategy type
const STRATEGY_THEMES: Record<string, {
    borderColor: string;
    hoverBorder: string;
    selectedBg: string;
    iconBg: string;
    iconColor: string;
}> = {
    velocity_push: {
        borderColor: '#f59e0b',
        hoverBorder: '#fcd34d',
        selectedBg: 'rgba(245, 158, 11, 0.05)',
        iconBg: '#fef3c7',
        iconColor: '#d97706',
    },
    logistics_lean: {
        borderColor: '#3b82f6',
        hoverBorder: '#93c5fd',
        selectedBg: 'rgba(59, 130, 246, 0.05)',
        iconBg: '#dbeafe',
        iconColor: '#2563eb',
    },
    service_first: {
        borderColor: '#10b981',
        hoverBorder: '#6ee7b7',
        selectedBg: 'rgba(16, 185, 129, 0.05)',
        iconBg: '#d1fae5',
        iconColor: '#059669',
    },
};

// Metric configuration for dynamic rendering
const METRIC_CONFIG: Record<string, { label: string; icon: React.ReactElement; format: 'cost' | 'days' | 'score'; isDelta?: boolean; getColors: (val: number) => { color: string; bg: string; border: string } }> = {
    delta_transport_cost: {
        label: 'Transport',
        icon: <TruckIcon sx={{ fontSize: 14 }} />,
        format: 'cost',
        getColors: (v) => v > 0 ? { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' } : { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' }
    },
    delta_holding_cost: {
        label: 'Holding',
        icon: <BoxIcon sx={{ fontSize: 14 }} />,
        format: 'cost',
        getColors: (v) => v > 0 ? { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' } : { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' }
    },
    delta_sell_through: {
        label: 'Sell-Thru',
        icon: <ActivityIcon sx={{ fontSize: 14 }} />,
        format: 'days',
        getColors: (v) => v < 0 ? { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' } : { color: '#475569', bg: '#f8fafc', border: '#e2e8f0' }
    },
    delta_fulfillment: {
        label: 'Fulfillment',
        icon: <BoxIcon sx={{ fontSize: 14 }} />,
        format: 'cost',
        getColors: (v) => v >= 0 ? { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' } : { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' }
    },
    delta_wait_time: {
        label: 'Wait Time',
        icon: <ActivityIcon sx={{ fontSize: 14 }} />,
        format: 'days',
        getColors: (v) => v <= 0 ? { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' } : { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' }
    },
    equity_score: {
        label: 'Equity Score',
        icon: <ShieldIcon sx={{ fontSize: 14 }} />,
        format: 'score',
        isDelta: false,
        getColors: () => ({ color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd' })
    },
    delta_build_efficiency: {
        label: 'Efficiency',
        icon: <ZapIcon sx={{ fontSize: 14 }} />,
        format: 'cost',
        getColors: (v) => v >= 0 ? { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' } : { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' }
    },
    policy_compliance: {
        label: 'Compliance',
        icon: <ShieldIcon sx={{ fontSize: 14 }} />,
        format: 'score',
        isDelta: false,
        getColors: () => ({ color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' })
    },
    delta_units_rebalanced: {
        label: 'Rebalancing',
        icon: <BoxIcon sx={{ fontSize: 14 }} />,
        format: 'cost',
        getColors: (v) => v >= 0 ? { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' } : { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' }
    }
};
const METRIC_ORDER = [
    'delta_transport_cost',
    'delta_holding_cost',
    'delta_sell_through',
    'delta_fulfillment',
    'delta_wait_time',
    'delta_build_efficiency',
    'delta_units_rebalanced',
    'equity_score',
    'policy_compliance',
];

const DEFAULT_THEME = {
    borderColor: '#6366f1',
    hoverBorder: '#a5b4fc',
    selectedBg: 'rgba(99, 102, 241, 0.05)',
    iconBg: '#e0e7ff',
    iconColor: '#4f46e5',
};

export const StrategyCard: React.FC<StrategyCardProps> = ({
    strategy,
    isSelected,
    onSelect
}) => {
    const [showRationale, setShowRationale] = useState(false);
    const theme = STRATEGY_THEMES[strategy.id] || DEFAULT_THEME;

    const getIcon = (id: string) => {
        const iconSx = { fontSize: 22, color: theme.iconColor };
        switch (id) {
            case 'velocity_push': return <ZapIcon sx={iconSx} />;
            case 'logistics_lean': return <TruckIcon sx={iconSx} />;
            case 'service_first': return <ShieldIcon sx={iconSx} />;
            default: return <BoxIcon sx={iconSx} />;
        }
    };

    const formatImpact = (val: number, format: 'cost' | 'days' | 'score') => {
        if (format === 'score') {
            const normalizedScore = val <= 1.5 ? val * 100 : val;
            return `${Math.round(normalizedScore)}%`;
        }
        if (format === 'cost') {
            const normalized = Math.abs(val) < 0.0005 ? 0 : val;
            const sign = normalized >= 0 ? '+' : '';
            return `${sign}${(normalized * 100).toFixed(0)}%`;
        }
        const normalizedDays = Math.abs(val) < 0.05 ? 0 : val;
        const sign = normalizedDays >= 0 ? '+' : '';
        const rounded = Math.round(normalizedDays * 10) / 10;
        return `${sign}${rounded.toFixed(1)} days`;
    };

    const isDisplayZero = (val: number, format: 'cost' | 'days' | 'score') => {
        if (format === 'score') return false;
        if (format === 'cost') {
            return Math.abs(val) < 0.005; // rounds to 0% with current formatter
        }
        return Math.abs(val) < 0.05; // rounds to 0.0 days with current formatter
    };

    const metricStateEntries = (strategy.impact_metric_states && strategy.impact_metric_states.length > 0)
        ? strategy.impact_metric_states
            .filter((m) => Boolean(METRIC_CONFIG[m.key]))
            .sort((a, b) => METRIC_ORDER.indexOf(a.key) - METRIC_ORDER.indexOf(b.key))
        : Object.entries(strategy.impact_estimates || {})
            .filter(([key, val]) => Boolean(METRIC_CONFIG[key]) && val !== undefined)
            .sort(([a], [b]) => METRIC_ORDER.indexOf(a) - METRIC_ORDER.indexOf(b))
            .map(([key, value]) => ({ key, status: 'computed' as const, value }));

    const computedMetricEntries = metricStateEntries.filter((m) => m.status === 'computed' && m.value !== undefined);
    const noChangeEntries = metricStateEntries.filter((m) => m.status === 'no_material_change');
    const noChangeCount = noChangeEntries.length;
    const unavailableCount = metricStateEntries.filter((m) => m.status === 'unavailable').length;
    const showConvergencePanel = computedMetricEntries.length === 0 && noChangeCount >= 2 && noChangeCount >= metricStateEntries.length - 1;
    const getNoChangeTooltip = (label: string) =>
        `${label} was simulated, but the preview delta is below the material-change threshold.`;

    return (
        <Paper
            elevation={isSelected ? 4 : 1}
            onClick={() => onSelect(strategy.id)}
            sx={{
                position: 'relative',
                cursor: 'pointer',
                borderRadius: 3,
                border: '1px solid',
                borderColor: isSelected ? theme.borderColor : '#e2e8f0',
                borderLeftWidth: isSelected ? 4 : 1,
                bgcolor: isSelected ? theme.selectedBg : '#fff',
                overflow: 'visible',  // Allow badge to extend outside
                transition: 'all 0.25s ease',
                mt: 1.5,  // Add top margin to give space for the badge
                '&:hover': {
                    borderColor: isSelected ? theme.borderColor : theme.hoverBorder,
                    transform: isSelected ? 'none' : 'translateY(-4px)',
                    boxShadow: isSelected ? 4 : 6,
                },
            }}
        >
            {/* Recommendation Badge - Overlays card corner */}
            {strategy.is_recommended && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -8,
                        left: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.5,
                        py: 0.5,
                        bgcolor: '#f59e0b',
                        color: '#fff',
                        borderRadius: 5,
                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
                        zIndex: 10,
                    }}
                >
                    <SparklesIcon sx={{ fontSize: 12 }} />
                    <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Strategist's Choice
                    </Typography>
                </Box>
            )}

            {/* Selection Indicator - Bottom Right */}
            {isSelected && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 12,
                        right: 12,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 2,
                        zIndex: 10,
                    }}
                >
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#fff' }} />
                </Box>
            )}

            {/* Card Content */}
            <Box sx={{ p: 2.5, pt: 2.5 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                    <Box
                        sx={{
                            p: 1.25,
                            borderRadius: 2,
                            bgcolor: theme.iconBg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {getIcon(strategy.id)}
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b', lineHeight: 1.3, mb: 0.5 }}>
                            {strategy.title}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                            {strategy.description}
                        </Typography>
                    </Box>
                </Box>

                {/* Metrics Pills - Dynamic Rendering */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 96, alignContent: 'flex-start' }}>
                    {showConvergencePanel && (
                        <Box
                            sx={{
                                width: '100%',
                                border: '1px dashed #cbd5e1',
                                bgcolor: '#f8fafc',
                                borderRadius: 2,
                                px: 1.25,
                                py: 1,
                            }}
                        >
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#334155', lineHeight: 1.2 }}>
                                Preflight outcomes are mostly similar
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: '#64748b', lineHeight: 1.4, mt: 0.25 }}>
                                The same three metrics were computed for this strategy preview; most are below the material-change threshold.
                            </Typography>
                        </Box>
                    )}
                    {metricStateEntries.map((metric) => {
                        const config = METRIC_CONFIG[metric.key];
                        if (!config) return null;
                        const isComputed = metric.status === 'computed' && metric.value !== undefined;
                        const value = metric.value as number | undefined;
                        const isRoundedZeroComputed = isComputed && isDisplayZero(value as number, config.format);
                        const colors = isComputed
                            ? (isRoundedZeroComputed
                                ? { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' }
                                : config.getColors(value as number))
                            : { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' };
                        const prefix = config.isDelta === false ? '' : 'Δ ';
                        const suffixLabel = isComputed
                            ? (isRoundedZeroComputed && config.isDelta !== false
                                ? 'No material change'
                                : formatImpact(value as number, config.format))
                            : (metric.status === 'no_material_change' ? 'No material change' : 'Unavailable');
                        const chip = (
                            <Chip
                                key={metric.key}
                                size="small"
                                icon={config.icon}
                                label={`${prefix}${config.label} ${suffixLabel}`}
                                variant={(isComputed && !isRoundedZeroComputed) ? 'filled' : 'outlined'}
                                sx={{
                                    height: 26, fontSize: 11, fontWeight: 600,
                                    bgcolor: colors.bg,
                                    color: colors.color,
                                    border: '1px solid',
                                    borderColor: colors.border,
                                    '& .MuiChip-icon': { color: 'inherit' },
                                }}
                            />
                        );
                        const chipShowsNoChange = metric.status === 'no_material_change' || isRoundedZeroComputed;
                        if (!chipShowsNoChange) {
                            return chip;
                        }
                        return (
                            <Tooltip
                                key={metric.key}
                                arrow
                                placement="top"
                                title={getNoChangeTooltip(config.label)}
                            >
                                <span onClick={(e) => e.stopPropagation()}>{chip}</span>
                            </Tooltip>
                        );
                    })}
                    {unavailableCount > 0 && (
                        <Typography sx={{ width: '100%', fontSize: 10, color: '#94a3b8', lineHeight: 1.35 }}>
                            {unavailableCount} metric{unavailableCount === 1 ? '' : 's'} unavailable in preflight preview.
                        </Typography>
                    )}
                </Box>

                {/* Collapsible Rationale */}
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowRationale(!showRationale);
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            mx: -1,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                            transition: 'all 0.15s',
                        }}
                    >
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Strategic Rationale
                        </Typography>
                        {showRationale ? (
                            <ExpandLessIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                        ) : (
                            <ExpandMoreIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                        )}
                    </Box>
                    <Collapse in={showRationale}>
                        <Typography sx={{ fontSize: 11, color: '#64748b', lineHeight: 1.6, mt: 1, fontStyle: 'italic' }}>
                            "{strategy.reasoning}"
                        </Typography>
                    </Collapse>
                </Box>
            </Box>
        </Paper >
    );
};
